<template>
  <div class="dropdown-button" ref="container">
    <button
      ref="trigger"
      class="dropdown-trigger"
      :class="triggerClass"
      :disabled="disabled"
      @click.prevent.stop="toggle"
    >
      <vue-feather :type="icon" size="18" />
    </button>
    <FloatingDropdown ref="dropdown" :open="isOpen" :trigger-ref="$refs.trigger" :match-width="false" :align="align" @dismiss="close">
      <div class="action-dropdown-menu" :class="{ 'action-dropdown-menu--small': size === 'small' }">
        <div
          v-for="(item, index) in regularItems"
          :key="item.key || index"
          class="dropdown-item"
          :class="{ 'dropdown-item--disabled': item.disabled }"
          v-tooltip="item.tooltip || ''"
          @click="handleItemClick(item, index)"
        >
          <vue-feather v-if="item.icon" :type="item.icon" size="18" class="dropdown-item-icon" />
          <span>{{ item.label }}</span>
        </div>
        <div
          v-for="(item, index) in dangerItems"
          :key="item.key || `danger-${index}`"
          class="dropdown-item dropdown-item--danger"
          :class="{ 'dropdown-item--disabled': item.disabled }"
          @click="handleItemClick(item, regularItems.length + index)"
        >
          <vue-feather v-if="item.icon" :type="item.icon" size="18" class="dropdown-item-icon" />
          <span>{{ item.label }}</span>
        </div>
      </div>
    </FloatingDropdown>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import { FloatingDropdown } from '@/design'

export default {
  name: 'DropdownButton',
  components: {
    'vue-feather': VueFeather,
    FloatingDropdown
  },
  props: {
    // Array of menu items: { key, label, icon?, danger?, disabled? }
    items: {
      type: Array,
      required: true,
      validator: (items) => items.every(item => item.key && item.label)
    },
    // Button variant: 'default', 'primary'
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'primary'].includes(value)
    },
    // Feather icon for trigger button
    icon: {
      type: String,
      default: 'more-horizontal'
    },
    // Menu alignment: 'left' or 'right'
    align: {
      type: String,
      default: 'right',
      validator: (value) => ['left', 'right'].includes(value)
    },
    // Size: 'default', 'small'
    size: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'small'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false
    }
  },
  computed: {
    triggerClass() {
      return {
        'dropdown-trigger--primary': this.variant === 'primary',
        'dropdown-trigger--small': this.size === 'small',
        'dropdown-trigger--open': this.isOpen
      }
    },
    regularItems() {
      return this.items.filter(item => !item.danger)
    },
    dangerItems() {
      return this.items.filter(item => item.danger)
    }
  },
  mounted() {
    window.addEventListener('click', this.handleOutsideClick)
  },
  unmounted() {
    window.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    toggle() {
      if (this.disabled) return
      this.isOpen = !this.isOpen
      this.$emit(this.isOpen ? 'open' : 'close')
    },
    close() {
      this.isOpen = false
      this.$emit('close')
    },
    handleOutsideClick(event) {
      if (!this.isOpen) return
      const container = this.$refs.container
      if (container && !container.contains(event.target) && !this.$refs.dropdown?.containsElement(event.target)) {
        this.close()
      }
    },
    handleItemClick(item, index) {
      if (item.disabled) return
      this.close()
      this.$emit('select', item, index)
    }
  },
  emits: ['open', 'close', 'select']
}
</script>

<style scoped>
.dropdown-button {
  position: relative;
  display: inline-block;
}

.dropdown-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 45px;
  min-height: 45px;
  padding: 0;
  border-radius: 4px;
  background: #FFF;
  border: 1px solid #d1d5db;
  outline: none;
  cursor: pointer;
  color: #374151;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.dropdown-trigger:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.dropdown-trigger:active:not(:disabled) {
  transform: scale(0.98);
}

.dropdown-trigger--open {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.dropdown-trigger--small {
  min-width: 32px;
  min-height: 32px;
}

.dropdown-trigger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Primary variant */
.dropdown-trigger--primary {
  background: var(--color-primary, #6a16f0);
  color: #FFF;
  border: none;
  box-shadow: 0 1px 2px rgba(106, 22, 240, 0.15);
}

.dropdown-trigger--primary:hover:not(:disabled) {
  background: #5c11d5;
  box-shadow: 0 2px 4px rgba(106, 22, 240, 0.25);
}


</style>

<style>
/* Menu - not scoped so it applies when teleported */
.action-dropdown-menu {
  background: #FFF;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 160px;
  padding: 4px;
}

.action-dropdown-menu .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 4px;
  white-space: nowrap;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  transition: background 0.1s ease;
}

.action-dropdown-menu .dropdown-item:hover {
  background: #f3f4f6;
}

.action-dropdown-menu .dropdown-item--danger {
  color: #dc2626;
}

.action-dropdown-menu .dropdown-item--danger:hover {
  background: #fef2f2;
}

.action-dropdown-menu .dropdown-item--disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.action-dropdown-menu .dropdown-item--disabled:hover {
  background: transparent;
}

.action-dropdown-menu .dropdown-item-icon {
  flex-shrink: 0;
}


/* Small menu */
.action-dropdown-menu--small {
  min-width: 140px;
  padding: 3px;
}

.action-dropdown-menu--small .dropdown-item {
  padding: 8px 10px;
  font-size: 0.8125rem;
  gap: 8px;
}
</style>
